import { nameByPlatform } from "~/constants/platfroms";
import { colorByPlatform } from "~/feature/chart/constants";
import {
  type ChartSide,
  ChartViewEnum,
  type IMarketGroupChanceHistory,
} from "~/feature/chart/types";
import { isNoSide, sidesByViewMode } from "~/feature/chart/utils";
import type { IMarket } from "~/types/event";
import type { PlatformEnum } from "~/types/platform";
import { formatPercentageValue } from "~/utils/formatValue";

export interface IChartTooltipRow {
  key: string;
  color: string;
  label: string;
  sublabel: string;
  chanceLabel: string;
  isConsensus: boolean;
}

export interface IChartTooltipBox {
  title: "YES" | "NO" | null;
  rows: IChartTooltipRow[];
}

export interface IBuildChartTooltipParams {
  markets: IMarket[];
  selections: string[];
  chanceHistory: IMarketGroupChanceHistory[];
  hoveredTimestamp: number | null;
  viewMode: ChartViewEnum;
  consensusColor: string;
}

type HoveredPoint = IMarketGroupChanceHistory["historical"][number];

const percent = (value: number) =>
  formatPercentageValue(value, { maximumFractionDigits: 1 });

const getHoveredPoints = (
  chanceHistory: IMarketGroupChanceHistory[],
  hoveredTimestamp: number
): HoveredPoint[] =>
  chanceHistory.flatMap(({ historical }) =>
    historical.filter(
      ({ timestamp }) => Date.parse(timestamp) === hoveredTimestamp
    )
  );

const buildSideBox = (
  markets: IMarket[],
  selections: string[],
  points: HoveredPoint[],
  side: ChartSide,
  withTitle: boolean
): IChartTooltipBox => {
  const chancesByMarketId = new Map(
    points
      .flatMap(({ byPlatform }) => byPlatform)
      .map((chance) => [chance.marketId, chance])
  );

  return {
    title: withTitle ? (isNoSide(side) ? "NO" : "YES") : null,
    rows: markets
      .filter(({ id }) => selections.includes(id))
      .flatMap((market) => {
        const chance = chancesByMarketId.get(market.id);

        if (!chance) {
          return [];
        }

        const platform = market.platform as PlatformEnum;

        return [
          {
            key: market.id,
            color: colorByPlatform[platform],
            label: market.title || "",
            sublabel: nameByPlatform[platform],
            chanceLabel: percent(
              isNoSide(side) ? chance.noChance : chance.yesChance
            ),
            isConsensus: false,
          },
        ];
      }),
  };
};

const buildConsensusRow = (
  markets: IMarket[],
  selections: string[],
  points: HoveredPoint[],
  consensusColor: string
): IChartTooltipRow[] => {
  const consensusYes = points.at(0)?.consensus?.yesChance;
  const groupTitle = markets.find(({ id }) => selections.includes(id))?.title;

  if (consensusYes === undefined || !groupTitle) {
    return [];
  }

  return [
    {
      key: "consensus",
      color: consensusColor,
      label: groupTitle,
      sublabel: "consensus",
      chanceLabel: percent(consensusYes),
      isConsensus: true,
    },
  ];
};

export const buildChartTooltip = ({
  markets,
  selections,
  chanceHistory,
  hoveredTimestamp,
  viewMode,
  consensusColor,
}: IBuildChartTooltipParams): IChartTooltipBox[] => {
  if (hoveredTimestamp === null) {
    return [];
  }

  const points = getHoveredPoints(chanceHistory, hoveredTimestamp);
  const sides = sidesByViewMode[viewMode];
  const withTitles = viewMode === ChartViewEnum.BOTH;

  const orderedSides = withTitles ? [...sides].reverse() : sides;

  const boxes = orderedSides.map((side) =>
    buildSideBox(markets, selections, points, side, withTitles)
  );

  if (viewMode === ChartViewEnum.CONSENSUS) {
    boxes[0].rows.push(
      ...buildConsensusRow(markets, selections, points, consensusColor)
    );
  }

  return boxes.filter(({ rows }) => rows.length);
};
